<script type="ts">
  import FaArrowCircleLeft from "svelte-icons/fa/FaArrowCircleLeft.svelte";
  import FaChartLine from "svelte-icons/fa/FaChartLine.svelte";
  import FaPercent from "svelte-icons/fa/FaPercent.svelte";
  import FaArrowCircleRight from "svelte-icons/fa/FaArrowCircleRight.svelte";
  import FaRegCalendarAlt from "svelte-icons/fa/FaRegCalendarAlt.svelte";
  import FaShareAlt from "svelte-icons/fa/FaShareAlt.svelte";
  import { onMount, type ComponentProps } from "svelte";
  import { visualizationDataStore, showHeaderStore } from "../stores";
  import { fade } from "svelte/transition";
  import Button from "../../ui-components/button.svelte";
  import FilterSelect from "../../ui-components/filter-select.svelte";
  import IconDropdown from "../../ui-components/icon-dropdown.svelte";
  import Loader from "../../ui-components/loader.svelte";
  import VisualizationContainer from "../../ui-components/visualization-container.svelte";
  import CustomPeriodSelector from "../../ui-components/custom-period-selector.svelte";
  import { MD5 } from "../../utils/md5";
  import {
    ChartType,
    ExportType,
    type TimeSeries,
    type TimeSeriesChartInterface,
  } from "../chart-interfaces";
  import { createChartConfig } from "../chart/chart-config";
  import type {
    ChartData,
    VisualizationData,
    DataSourceInterface,
  } from "../data-source-interface";
  import { monthShortToLongDict, monthShortList } from "../data-source-interface";
  import {
    createDateLabel,
    createValueLabel,
    getTimeSeriesName,
    numberFormatter,
  } from "../chart/labels";
  import {
    DataValueType,
    DateFilter,
    Share,
  } from "../options";
  import type { SelectorSelections, Options, SeriesOptions } from "../types";
  import CustomError from "../../ui-components/custom-error.svelte";
  import { 
    selectorSelectionsToSeriesOptions,
    sortObject,
    keyOf,
    generateUID,
    createQuery,
    sortByDisabledAndSelected,
    sortOptionsAlphabetically,
    updateProps,
    handleHeader,
  } from "../utils";

  import { t, locale } from "../../translations/i18n";
 
  export let chart: TimeSeriesChartInterface;
  export let dataSource: DataSourceInterface;

  /** Control when "Add data" & "Back" buttons are shown in the selector */
  export let showSelectorButtons: boolean;

  /** FilterSelect props in this collection are rendered into the selector */
  export let filterSelectProps: ComponentProps<FilterSelect>[] = [];
  
  /** Reload the filter select props when the filterSelectPropsChanged variable is changed. */
  let filterSelectPropsChanged: boolean = false;

  // If the view is being restored from a previous session, the data is loaded from the storage
  export let restoreView: boolean = false;
  export let storageData: any = [];

  /** Default options for the UI */
  const options: Options = {
    chartType: ChartType.LINE,
    dataValueType: DataValueType.ABSOLUTE,
    dateFilter: [DateFilter.MONTHS],
  };

  /** Selections made on the selector */
  let selectorSelections: SelectorSelections = { statName: "", statVerboseName: {}, is_daily: false, selections: {} };

  /** Initial state for the loaders */
  let showContainerLoader: boolean = false;
  let showSelectorLoader: boolean = false;

  /** Unique id for each visualization */
  let uniqueId: string = restoreView ? storageData.id : generateUID();

  let visualizationData: VisualizationData;

  /** Nav buttons are inactivated until first data is loaded */
  let navButtonsDisabled: boolean = true;

  /** Period selector button disable control in case of daily data */
  let isDaily: boolean = false;

  /** "Add data" button should be disabled until there is a selection on each field */
  let addDataButtonDisabled: boolean = true;

  /** Showing an error message when the data is unavailable. */
  let error_message: boolean = false;

  /** Show additional error info on restoreDataHandler error */
  let restoreDataError: boolean = false;

  /** Collection all metadata from chart */
  let metadata_array: any;

  /** Check wheather to show to custom period selector */
  let customPeriodSelected: boolean = false;
  $: showCustomPeriodSelector = customPeriodSelected;

  let currentContainer: any;

  let chartContainer: HTMLDivElement;

  /** Variable containing the current language of the page */
  let currentLanguage: any = $locale.split("-")[0]

  /** Variable containing the current focused element index */
  let focusedElementIndex: number = 0;

  /** Functionality for hiding headers */
  let showHeader: any;
  showHeaderStore.subscribe(value => {
    showHeader = value;
  });
  $: handleHeader(showHeader, uniqueId);

  /**
   * Handler for ui components to add data to the chart
   * 
   * @param visualization - The visualization metadata to restore
   */
  const addDataHandler = async (): Promise<void> => {
    try {
      showContainerLoader = true;
      error_message = false;

      const seriesOptions = selectorSelectionsToSeriesOptions(selectorSelections);

      // If any daily data added to the table, then the period selector will be disabled.
      if (selectorSelections.is_daily){
        isDaily = true;
      }

      const timeSeries = await Promise.all(
        seriesOptions.map(async (seriesOption) => {
          const query = createQuery(options, seriesOption);
          const queryData = await dataSource.getChartData(query);

          return {
            id: createId(seriesOption),
            name: getTimeSeriesName(seriesOption, seriesOption.statVerboseName[currentLanguage]),
            type: options.chartType,
            data: mapToTimeSeriesData(queryData, query.seriesOptions.is_daily),
            metadata: seriesOption,
          };
        })
      );

      if (Object.keys(timeSeries[0].data).length === 0){
        // Show error message if no data is available.
        error_message = true;
      }
      else {
        // Show chart view if data is available.
        const chartConfig = createChartConfig(options);
        chart.addTimeSeries(timeSeries, chartConfig);
        showContainerLoader = false;
        navButtonsDisabled = false;
        storeVisualizationData(selectorSelections);

        // Handle header depending on showHeader state
        handleHeader(showHeader, uniqueId);
      }
      resetSelector();
    } catch (error) {
      error_message = true;
      console.error("Error in addDataHandler: ", error);
    }
  };

  /**
   * Handler for ui components to restore saved data to the chart
   */
  const restoreDataHandler =  async(visualization: any): Promise<void> => {
    try {
      // Update metadata array with local storage metadata
      showContainerLoader = true;
      error_message = false;
      
      for (let i = 0; i < visualization.metadata.length; i++){
        const seriesOptions = selectorSelectionsToSeriesOptions(visualization.metadata[i]);

        // If any daily data added to the table, then the period selector will be disabled.
        if (visualization.metadata[i].is_daily){
          isDaily = true;
        }

        const timeSeries = await Promise.all(
          seriesOptions.map(async (seriesOption) => {
            const query = createQuery(options, seriesOption);
            const queryData = await dataSource.getChartData(query);

            return {
              id: createId(seriesOption),
              name: getTimeSeriesName(seriesOption, seriesOption.statVerboseName[currentLanguage]),
              type: options.chartType,
              data: mapToTimeSeriesData(queryData, query.seriesOptions.is_daily),
              metadata: seriesOption,
            };
          })
        );

          if (Object.keys(timeSeries[0].data).length === 0){
            // Show error message if no data is available.
            restoreDataError = true;
            error_message = true;
          }
          else {
            // Show chart view if data is available.
            const chartConfig = createChartConfig(options);
            chart.addTimeSeries(timeSeries, chartConfig);

            // Handle header depending on showHeader state
            handleHeader(showHeader, uniqueId);

          }
      }
      resetSelector();
      showContainerLoader = false;
      navButtonsDisabled = false;
    } catch (error) {
      restoreDataError = true;
      error_message = true;
      console.error("Error in restoreDataHandler: ", error);
    }
  };


  /**
   * Store metadata of the visualization
   * @param selectorSelections - Selections made on the selector
   * @returns void
   */
  const storeVisualizationData = (selectorSelections: SelectorSelections): void => {
    let storedVisualizationData: VisualizationData[] = [];
    let containerSize: string = 'h-large-v-small';
    visualizationDataStore.subscribe(
      (value) => {
        storedVisualizationData = value;
      }
    );

    const existingIndex = storedVisualizationData.findIndex((item) => item.id === uniqueId);
    if (existingIndex !== -1) {
      containerSize = storedVisualizationData[existingIndex].visualizationSize ?? '';
    };

    metadata_array.push(selectorSelections);
    visualizationData = {
      id: uniqueId,
      type: "Chart",
      metadata: metadata_array,
      period: options.dateFilter,
      dataValueType: options.dataValueType,
      chartType: options.chartType,
      visualizationSize: containerSize,
    };

    visualizationDataStore.updateCollection(visualizationData);
  };

  /**
   * Update Period(Date Filter), Chart Type and Value Type of Chart visualization to store on browser database.
   * @param period - Period(Date Filter) of the Chart visualization
   */
   const updateChartOptionStore = (period: string[]): void => {
    visualizationData.period = period; 
    visualizationData.dataValueType = options.dataValueType;
    visualizationData.chartType = options.chartType;
    visualizationDataStore.updateCollection(visualizationData);
  };

  /**
   * Handler for ui components to update data in the chart
   */
  const updateDataHandler = async (): Promise<void> => {

    if (String(options.dateFilter).includes('-')) {
      // Convert date from string to DateFilter list when the filter has multiple months
      let [startMonth, endMonth] = String(options.dateFilter).split(" - ");
      startMonth = monthShortToLongDict[startMonth];
      endMonth = monthShortToLongDict[endMonth];
      let dateFilterList : DateFilter[] = [startMonth, endMonth] as DateFilter[];
      options.dateFilter = dateFilterList;
    }
    // Convert date from string to DateFilter list when the filter is not custom
    if (typeof(options.dateFilter) !== "object" && options.dateFilter !== undefined){
      const input: string = options.dateFilter;
      let dateFilter: DateFilter = input as DateFilter;
      options.dateFilter = [dateFilter];
    }

    showContainerLoader = true;

    const timeSeries = chart.getTimeSeries();
    const updateTimeSeries = await Promise.all(
      timeSeries.map(async (ts) => {
        const seriesOptions = ts.metadata as SeriesOptions; // contains the SeriesOptions used to create the series;
        const query = createQuery(options, seriesOptions);
        const queryData = await dataSource.getChartData(query);
        return {
          id: ts.id,
          type: options.chartType,
          data: mapToTimeSeriesData(queryData, query.seriesOptions.is_daily),
        };
      })
    );

    const chartConfig = createChartConfig(options);
    chart.updateTimeSeries(updateTimeSeries, chartConfig);

    updateChartOptionStore(options.dateFilter);
    showContainerLoader = false;
  };

  /**
   * Export menu handler
   */
  const exportHandler = (type: any): void => {
    switch (type) {
      case Share.PNG:
        chart.export(ExportType.PNG);
        break;
      case Share.SVG:
        chart.export(ExportType.SVG);
        break;
      case Share.PRINT:
        chart.print();
        break;
      case Share.CSV:
        exportCSV();
        break;
    }
  };

  /**
   * Function to export table as CSV file.
  */
  const exportCSV = (): void => {
    const separator = ";";
    const filename = "VisitoryData.csv";

    function percentageToDecimal(element: string, delimiter: string = '.'): string {
      return element.endsWith('%') ?
        (parseFloat(element.replace(" %", '')) / 100).toString().replace('.', delimiter) :
        element;
    }

    const timeSeries = chart.getTimeSeries();
    if (!timeSeries || timeSeries.length === 0) return;

    // Build headers
    const locationList: string[] = timeSeries
      .map((s: any) => {
        const vLoc = s.metadata?.verbose_options?.location;
        const oLoc = s.metadata?.options?.location;
        const loc = (vLoc ?? oLoc ?? "").toString().trim();
        return loc;
      })
      .filter((loc: string) => loc.length > 0);
    const uniqueLocations = Array.from(new Set(locationList));
    const multipleLocations = uniqueLocations.length > 1;

    const formattedSeriesNames: string[] = timeSeries.map((s: any, i: number) => {
      const plainName = String(s.metadata?.statVerboseName?.[currentLanguage] ?? s.name);
      const baseName = plainName.replace(/\s*\(.*\)\s*$/, "");
      const vLoc = s.metadata?.verbose_options?.location;
      const oLoc = s.metadata?.options?.location;
      const loc = (vLoc ?? oLoc ?? "").toString().trim();
      const suffix = multipleLocations && loc ? ` (${loc})` : "";
      return `${i + 1}. ${baseName}${suffix}`;
    });

    // Collect timestamps and label lookup
    const allTimesSet = new Set<number>();
    const labelLookup = new Map<number, string>();
    timeSeries.forEach((s: any) => {
      (s.data || []).forEach((p: any) => {
        if (p && p.date instanceof Date) {
          const t = p.date.getTime();
          allTimesSet.add(t);
          if (!labelLookup.has(t) && p.dateLabel) labelLookup.set(t, p.dateLabel);
        }
      });
    });

    // Sort correctly
    const allTimes = Array.from(allTimesSet).sort((a, b) => b - a);

    // Build valueLabel lookups per series for fast access and locale formatting
    const valueLookups: Map<number, string>[] = timeSeries.map((s: any) =>
      new Map<number, string>((s.data || []).map((p: any) => [p.date.getTime(), p.valueLabel]))
    );

    // Header
    const lines: Array<Array<string>> = [];
    lines.push([$t("Date"), ...formattedSeriesNames]);

    // Rows
    for (const t of allTimes) {
      let dateLabel = labelLookup.get(t);
      if (!dateLabel) {
        const d = new Date(t);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        dateLabel = `${d.getFullYear()}-${mm}-${dd}`;
      }

      const row: Array<string> = [dateLabel];
      for (const lookup of valueLookups) {
        let valLabel = (lookup.get(t) ?? "").toString().trim();
        if (valLabel) {
          // Remove any non-breaking or thin spaces inside digit groups for all locales
          valLabel = valLabel.replace(/(\d)[\s\u00A0\u202F](?=\d)/g, '$1');
          switch (currentLanguage) {
            case "en":
              valLabel = valLabel.replaceAll(',', '');
              valLabel = percentageToDecimal(valLabel);
              break;
            case "fi":
              valLabel = percentageToDecimal(valLabel, ',');
              break;
            case "sv":
              valLabel = percentageToDecimal(valLabel, ',');
              break;
            case "nb":
              valLabel = percentageToDecimal(valLabel, ',');
              break;
          }
        }
        row.push(valLabel);
      }
      lines.push(row);
    }

    const csvContent = lines.map(r => r.join(separator)).join("\n");
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  /**
   * "Back button" handler
   *
   * Clicking back should take the user to the previous step in the selector.
   * For example, clicking back from the fields should show the statistics again.
   *
   * TODO: Implement more steps in the selector
   */
  const backHandler = (): void => {
    resetSelector();
  };

  /**
   * Add statistics to the selector
   */
  const addStatistics = async (): Promise<void> => {
    // Hide the "Add data" & "Back" buttons because clicking a stat takes the
    // user directly to the next "level"
    showSelectorButtons = false;
    showSelectorLoader = true;

    const statistics = await dataSource.getStatistics();
    // First, sort options alphabetically
    const sortedAlphabetically = sortOptionsAlphabetically(statistics.map((s) => ({
      model: s.model,
      name: s.name,
      title: s.description,
      keywords: s.keywords,
      disabled: s.disabled,
      is_daily: s.is_daily,
    })), true);

    // Then, sort the options by the disabled property
    sortedAlphabetically.sort((a: any, b: any) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1));

    filterSelectProps = [
      {
        title: $t("Statistics"),
        visualizationId: `container-id-${uniqueId}`,
        options: sortedAlphabetically,
        selectCallback: async (_title, selected, _selectClicked) => {
          const statName = selected?.[0]?.model || "";
          const statVerboseName = selected?.[0]?.name || "";
          const is_daily = selected?.[0]?.is_daily || false;
          selectorSelections.statName = statName;
          selectorSelections.statVerboseName = statVerboseName;
          selectorSelections.is_daily = is_daily;
          await addFields(statName);
        },
      },
    ];

    showSelectorLoader = false;

  };

  /**
   * Add fields into the selector
   *
   * @param statName - Name of the statistic
   */
  const addFields = async (statName: string): Promise<void> => {

    showSelectorLoader = true;
    let updatedFields: any = [];

    const fields = await dataSource.getFields(statName);
    filterSelectProps = fields.map((field) => {
      return {
        name: field.name,
        field: field.field,
        title: field.name[currentLanguage],
        visualizationId: `container-id-${uniqueId}`,
        options: sortOptionsAlphabetically(field.options.map((option) => ({
          name: option.name,
          disabled: option.disabled,
          parent_country: option.parent_country,
        })), true),
        multiselect: true,
        selectCallback: async (title, selected, _selectClicked) => {
          const values = selected?.map((s) => s.name["en"]) || [];
          const fieldVerboseName = selected?.map((s) => s.name) || [];
          selectorSelections.selections[field.field] = {
            values: fieldVerboseName as Object[],
            values_db: values as string[],
            verbose_key_name: field.name,
          };

          // Preventing user to click unavailable combinations of fields
          showSelectorLoader = true;

          // Call the function to get the disabled fields based on selection.
          updatedFields = await dataSource.updateDisabledFields(
            selectorSelections.selections,
            statName
          );

          showSelectorLoader = false;

          // Update the props of the fields
          filterSelectPropsChanged = !filterSelectPropsChanged;
          updateProps(updatedFields, filterSelectProps);

          // Sort the options alphabetically
          sortOptionsAlphabetically(filterSelectProps);

          // Sort the fields by disabled and selected values
          sortByDisabledAndSelected(filterSelectProps);

          // "Add data" button enabled if all fields have at least one selection
          addDataButtonDisabled =
            Object.keys(selectorSelections.selections).filter(
              (key) => selectorSelections.selections[key]["values_db"].length > 0
            ).length != fields.length;
        },
      };
    });

    showSelectorButtons = true;
    showSelectorLoader = false;
  };

  /**
   * Reset the selector to the initial state
   */
  const resetSelector = (): void => {
    addDataButtonDisabled = true;
    selectorSelections = { statName: "", statVerboseName: {}, is_daily: false, selections: {} };
    addStatistics();
  };

  /**
   * Create a unique id for the time series based on the options
   *
   * @param seriesOptions - the options used to create the id
   * @returns a unique string id
   */
  const createId = (seriesOptions: SeriesOptions): string => {
    return MD5(
      window.btoa(
        JSON.stringify([
          seriesOptions.statName,
          sortObject(seriesOptions.options),
        ])
      )
    );
  };

  /**
   * Map query data to time series data
   *
   * @param queryData - query data
   */
   const mapToTimeSeriesData = (queryData: ChartData, is_daily: boolean): TimeSeries["data"] => {
    const isChange = options.dataValueType === DataValueType.CHANGE;
    const isShare = false; // TODO: implement share
    return queryData.map((d) => {
      let dateLabel = "";
      if (is_daily){
        dateLabel = `${d.date.getDate()} ${$t(monthShortList[d.date.getMonth()])}, ${d.date.getFullYear()}`;
      } else {
        if (options.dateFilter.length == 2){
        dateLabel = `${$t(options.dateFilter[0])} - ${$t(options.dateFilter[1])}, ${d.date.getFullYear()}`;
        } else if ( options.dateFilter[0] == "Years"){
          dateLabel = `${d.date.getFullYear()}`;
        } else {
          dateLabel = createDateLabel(d.date, options.dateFilter[0], $locale);
      }
      }

      return {
        value: d.value,
        valueLabel: createValueLabel(d.value, d.unit || "", isChange, isShare),
        date: d.date,
        dateLabel: dateLabel,
        source: d.source,
      };
    });
  };

  /**
   * Handler for custom period selector close event
   * @param event
   */
   const customPeriodSelectorCloseHandler = (event : any) => {
    const { selectedFrom, selectedTo } = event.detail;
    if (selectedFrom == ''){
      customPeriodSelected = false;
    } else {
      options.dateFilter = [selectedFrom, selectedTo];
      updateDataHandler();
      customPeriodSelected = false;
    }
  };

  onMount(async () => {
    chart.init(chartContainer, numberFormatter)
    if (restoreView){
      // Set the metadata array to the metadata array from the storage.
      metadata_array = storageData.metadata;
      // Set the options to the options from the storage.
      options.dateFilter = storageData.period;
      options.chartType = storageData.chartType;
      options.dataValueType = storageData.dataValueType;
      // Set the visualization data to the visualization data from the storage.
      visualizationData = {
        id: storageData.id,
        type: "Chart",
        metadata: metadata_array,
        period: storageData.period,
        chartType: storageData.chartType,
        dataValueType: storageData.dataValueType,
        visualizationSize: storageData.visualizationSize,
      };

      // Call the function to restore the view.
      restoreDataHandler(storageData);
    } else {
      metadata_array= [];
      await addStatistics();
    }
  });

  /**
   * Handle keyboard events
   * @param event - The keyboard event
   * @returns void
  */
  const onKeyDown = (event: KeyboardEvent): void => {

    let visualisationContainer: HTMLElement = document.getElementById(`container-id-${uniqueId}`) as HTMLElement;
    let selectorButtons: NodeListOf<Element>  = visualisationContainer.querySelectorAll("#chart-selector-buttons button") as NodeListOf<Element>;
    focusedElementIndex = Array.from(selectorButtons).findIndex((element: Element) => 
      element === document.activeElement || element.contains(document.activeElement)
      );
      if (focusedElementIndex === -1) {
        focusedElementIndex = 0;
    }

    switch (event.key) {
      case "Enter":
        // Set the focus to the parent visualization container
        if (document.activeElement?.id.includes("AddData") || document.activeElement?.id.includes("back")){
          document.getElementById("container-id-" + uniqueId)?.focus();
        }
        break;

      case "ArrowDown":
        // Focus the next element in the list
        if (focusedElementIndex > 0) {
          (selectorButtons[focusedElementIndex - 1] as HTMLElement).focus();
        }
        else {
          // If the first element is focused, focus the last element
          (selectorButtons[selectorButtons.length - 1] as HTMLElement).focus();
        }
        break;

      case "ArrowUp":
        // Focus the previous element in the list
        if (focusedElementIndex < selectorButtons.length - 1) {
          (selectorButtons[focusedElementIndex + 1] as HTMLElement).focus();
        }
        else {
          // If the last element is focused, focus the first element
          (selectorButtons[0] as HTMLElement).focus();
        }
        break;

      case "ArrowLeft":
        // Focus to the filter select component
        let parentVisualizationContainer = document.getElementById(`container-id-${uniqueId}`) as HTMLElement;
        let filterPropsContainerList: NodeListOf<Element>  = parentVisualizationContainer.querySelectorAll('[id^="filterPropsContainer"]');
        // Set focus to the last filter select component
        (filterPropsContainerList[filterPropsContainerList.length - 1] as HTMLElement).focus();
        break;

      default:
        // Allow default behavior for other keys
        return;
    }

    // Prevent default behavior only for relevant keys
    event.preventDefault();
  };
</script>

<!-- Showing chart data when data is available. -->
{#if !error_message}
<VisualizationContainer
  showLoader={showContainerLoader}
  selectorDisabled={navButtonsDisabled}
  visualizationId={uniqueId}
  visualizationData={storageData}
  bind:this={currentContainer}
>
  <svelte:fragment slot="buttons">
    {#if showCustomPeriodSelector == true}
      <CustomPeriodSelector on:close={customPeriodSelectorCloseHandler}/>
    {/if}
    <IconDropdown
      state={options}
      key={keyOf(options.dataValueType, options)}
      options={DataValueType}
      handler={updateDataHandler}
      title={$t("Value format")}
      disabled={navButtonsDisabled}
    >
      <div class="icon" slot="icon"><FaPercent /></div>
    </IconDropdown>

    <IconDropdown
      state={options}
      key={keyOf(options.chartType, options)}
      options={ChartType}
      handler={updateDataHandler}
      title={$t("Chart type")}
      disabled={navButtonsDisabled}
      ><div class="icon" slot="icon"><FaChartLine /></div>
    </IconDropdown>

    {#if !isDaily}
      <IconDropdown
        state={options}
        key={keyOf(options.dateFilter, options)}
        options={DateFilter}
        handler={updateDataHandler}
        title={$t("Choose period")}
        bind:customPeriod={customPeriodSelected}
        disabled={navButtonsDisabled}
        ><div class="icon" slot="icon"><FaRegCalendarAlt /></div>
      </IconDropdown>
    {:else}
      <IconDropdown
        state={options}
        key={keyOf(options.dateFilter, options)}
        options={DateFilter}
        handler={updateDataHandler}
        title={$t("Choose period")}
        bind:customPeriod={customPeriodSelected}
        disabled={true}
        ><div class="icon" slot="icon"><FaRegCalendarAlt /></div>
      </IconDropdown>
    {/if}

    <IconDropdown
      options={Share}
      handler={exportHandler}
      title={$t("Share")}
      disabled={navButtonsDisabled}
    >
      <div class="icon" slot="icon"><FaShareAlt /></div>
    </IconDropdown>
  </svelte:fragment>

  <div class="selector" slot="selector">
    <Loader visible={showSelectorLoader} />

    <div class="filters">
      {#if filterSelectPropsChanged == true}
        {#each filterSelectProps as props (props.title)}
          <FilterSelect {...props} visualizationId={uniqueId}/>
        {/each}
      {:else if filterSelectPropsChanged == false}
        {#each filterSelectProps as props (props.title)}
          <FilterSelect {...props} visualizationId={uniqueId}/>
        {/each}
      {/if}
    </div>

    {#if showSelectorButtons}
      <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
      <div class="buttons" transition:fade id="chart-selector-buttons" tabindex="0" on:keydown={onKeyDown}>
        {#if filterSelectProps.length > 0}
          <Button
            handler={addDataHandler}
            iconOnTheLeft={false}
            disabled={addDataButtonDisabled}
            id="AddData-{uniqueId}"
          >
            <FaArrowCircleRight slot="icon" />
            <svelte:fragment slot="text">{$t("Show chart")}</svelte:fragment>
          </Button>
        {/if}
        <Button handler={backHandler} iconOnTheLeft={false} id="back-{uniqueId}">
          <FaArrowCircleLeft slot="icon" />
          <svelte:fragment slot="text">{$t("Back")}</svelte:fragment>
        </Button>
      </div>
    {/if}
  </div>

  <div class="chart-container" slot="content" bind:this={chartContainer}>
    {$t("Chart initialization failed")}
  </div>

</VisualizationContainer>
{/if}

<!-- Showing an error message when data is unavailable. -->
{#if error_message}
  <CustomError visualizationId={uniqueId} showAdditionalInfo={restoreDataError}>
    <p slot="additional-information" class="additional-error-info">{$t("This error might dissappear on page reload")}</p>
  </CustomError>
{/if}

<style lang="scss">
  .selector {
    display: flex;
    gap: 1rem;
    height: 100%;

    .filters {
      display: flex;
      gap: 0.5rem;
    }

    .buttons {
      display: flex;
      flex-direction: column;
      justify-content: stretch;
      gap: 1rem;

      :global(button:first-child) {
        flex: 1;
        background-color: var(--color-primary);
      }

      :global(button:last-child) {
        flex: 1;
        background-color: var(--color-tertiary);
      }
    }

    :global(.loader-container) {
      background-color: #232124;
    }
  }

  .chart-container {
    height: 100%;
  }

  .additional-error-info {
    margin: 0 0 30px 0;
  }
</style>
